import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { getGenerationPatterns } from '@platform/client';
import { generationPatternsSchema } from '@platform/schema/generation-ledger';
import { useQuery } from '@tanstack/react-query';

/**
 * What recurs across recent builds, each with the rule table's proposal — GEN-187.
 * It proposes and nothing more: the owner judges, and a card makes the change.
 */
export function GenerationPatterns() {
  const patterns = useQuery({
    queryKey: ['generation-patterns'],
    queryFn: async () =>
      generationPatternsSchema.parse(
        (await getGenerationPatterns({ throwOnError: true })).data,
      ),
  });

  return (
    <Paper variant="outlined" component="section" aria-label="Patterns">
      <Stack spacing={1} sx={{ p: 2 }}>
        <Typography variant="h6" component="h2">
          Patterns
        </Typography>
        {patterns.isPending && <LinearProgress />}
        {patterns.isError && (
          <Alert severity="error">The patterns could not be loaded.</Alert>
        )}
        {patterns.data !== undefined && (
          <Typography variant="body2" color="text.secondary">
            {`${String(patterns.data.builds)} builds in the last ${String(patterns.data.days)} days. Proposals come from a fixed rule table; nothing changes until you file a card.`}
          </Typography>
        )}
        {patterns.data?.patterns.length === 0 && (
          <Alert severity="info">Nothing recurs yet.</Alert>
        )}
        {patterns.data !== undefined && patterns.data.patterns.length > 0 && (
          <List dense disablePadding>
            {patterns.data.patterns.map((one) => (
              <ListItem key={one.text} disableGutters>
                <ListItemText
                  primary={one.text}
                  secondary={one.proposal ?? 'No proposal.'}
                  slotProps={{
                    secondary: {
                      color:
                        one.proposal === null
                          ? 'text.secondary'
                          : 'warning.main',
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Stack>
    </Paper>
  );
}
